import type { ApiClient } from '@carbonwebmail/api-client'
import type { ContactFieldListDto, SegmentDefinitionDto } from '@carbonwebmail/contracts'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLoaded } from '../../ui/useLoaded.js'
import type { Child, Rule } from './ruleVocabulary.js'
import { applyPatch } from './ruleVocabulary.js'

/**
 * Building a segment, and watching how many people it reaches.
 *
 * The reach is the feature. A rule list is abstract until a number moves beside
 * it — "not sent for 90 days" meaning 12 people rather than 12,000 is the
 * difference between sending and not, and finding that out after the send is
 * the expensive way.
 *
 * It is asked for on a debounce rather than on every keystroke. Each request is
 * a count over the whole contact table, and firing one per character would put
 * a query per keypress on a shared database for an answer nobody reads until
 * they stop typing.
 */

export interface BuilderState {
  readonly name: string
  readonly definition: SegmentDefinitionDto
  /** The same rules, each with the key it is rendered under. */
  readonly children: readonly Keyed[]
  /** The declared columns' values, or absent for a caller that does not edit them. */
  readonly custom: Readonly<Record<string, string>> | undefined
  /** Undefined until the first answer arrives, and while one is in flight. */
  readonly reach: number | undefined
  readonly problems: readonly string[]
  readonly saving: boolean
}

const EMPTY_RULE: Rule = { field: 'status', operator: 'is', value: 'subscribed' }

/**
 * A rule and the key React renders it under.
 *
 * The array index will not do. Two identical rules are legal — a builder is a
 * draft — so position is the only thing telling them apart, and keying by it
 * means removing a rule from the middle makes React reuse the wrong element:
 * focus jumps to a different row mid-sentence. A key minted when the rule is
 * created follows it instead.
 *
 * Stripped before the definition leaves this hook, so nothing downstream knows
 * the keys exist.
 */
export interface KeyedRule {
  readonly key: string
  readonly rule: Rule
}

/** A top-level entry: either a rule, or a bracket of rules. Both keyed. */
export type Keyed =
  | { readonly key: string; readonly rule: Rule }
  | { readonly key: string; readonly any: readonly KeyedRule[] }

export function isKeyedGroup(
  keyed: Keyed,
): keyed is { readonly key: string; readonly any: readonly KeyedRule[] } {
  return 'any' in keyed
}

/** Keys off, so nothing downstream knows they existed. */
function toChild(keyed: Keyed): Child {
  return isKeyedGroup(keyed) ? { any: keyed.any.map((inner) => inner.rule) } : keyed.rule
}

/** Long enough to finish a word, short enough to feel live. */
const REACH_DEBOUNCE_MS = 350

export function useSegmentBuilder({
  client,
  initial,
  segmentId,
  onSaved,
}: {
  readonly client: ApiClient
  readonly initial: {
    readonly name: string
    readonly definition: SegmentDefinitionDto
    /**
     * The declared columns' values, for a caller that edits them.
     *
     * **Absent means "never send `custom`"**, and that is what the builder page
     * passes. Two screens write a segment: the builder edits the rules and
     * knows nothing about declared columns, the board edits the columns and
     * does not touch the rules. A save that always sent this would make every
     * save from the builder wipe every value it had never been told about —
     * which is why the server treats an absent `custom` as "leave them alone".
     */
    readonly custom?: Readonly<Record<string, string>>
  }
  readonly segmentId: string | undefined
  readonly onSaved: () => void
}) {
  const nextKey = useRef(0)
  const mint = useCallback(() => {
    nextKey.current += 1
    return `k${nextKey.current}`
  }, [])

  const [name, setName] = useState(initial.name)
  const [match, setMatchValue] = useState(initial.definition.match)
  const [children, setChildren] = useState<readonly Keyed[]>(() =>
    initial.definition.children.map((child, index) =>
      'any' in child
        ? {
            key: `k0-${index}`,
            any: child.any.map((rule, inner) => ({ key: `k0-${index}-${inner}`, rule })),
          }
        : { key: `k0-${index}`, rule: child },
    ),
  )
  const [reach, setReach] = useState<number | undefined>(undefined)
  const [problems, setProblems] = useState<readonly string[]>([])
  const [custom, setCustomValues] = useState(initial.custom)

  /** Sets one declared column's value. Only a caller that was given `custom` has one. */
  const setCustom = useCallback((key: string, value: string) => {
    setCustomValues((current) => ({ ...current, [key]: value }))
  }, [])

  /**
   * The fields this organisation declared, so a rule can ask about one.
   *
   * A failure here is not fatal — every built-in field still works — and it is
   * not surfaced as an error, because the reach beside the rules is the thing
   * this screen is judged on. What it costs is the custom fields in the list.
   */
  const loadFields = useCallback(() => client.declaredFields('contacts'), [client])
  const { state: fields } = useLoaded<ContactFieldListDto>(
    loadFields,
    'We could not load your fields, so only the built-in ones are offered.',
  )
  const declared = fields.status === 'ready' ? fields.value.fields : []
  const [saving, setSaving] = useState(false)

  /** What everything downstream sees: the rules, without the render keys. */
  const definition: SegmentDefinitionDto = useMemo(
    () => ({ match, children: children.map(toChild) }),
    [match, children],
  )

  /**
   * Asks how many this reaches, after the typing stops.
   *
   * The cleanup both cancels the timer and marks the answer stale, so a slow
   * response for an older definition cannot land after a newer one and show a
   * number for rules that are no longer on screen.
   */
  useEffect(() => {
    let current = true
    const timer = setTimeout(() => {
      void client
        .segmentReach(definition)
        .then((value) => {
          if (current) setReach(value)
        })
        .catch(() => {
          // The count is an aid, not the screen. A failed one shows nothing
          // rather than replacing the answer with a zero that reads as "this
          // reaches nobody".
          if (current) setReach(undefined)
        })
    }, REACH_DEBOUNCE_MS)

    return () => {
      current = false
      clearTimeout(timer)
    }
  }, [client, definition])

  const setMatch = useCallback((next: 'all' | 'any') => {
    setMatchValue(next)
  }, [])

  const addRule = useCallback(() => {
    setChildren((current) => [...current, { key: mint(), rule: { ...EMPTY_RULE } }])
  }, [mint])

  const addGroup = useCallback(() => {
    setChildren((current) => [
      ...current,
      { key: mint(), any: [{ key: mint(), rule: { ...EMPTY_RULE } }] },
    ])
  }, [mint])

  /**
   * Changes one rule, at the top level or inside a group.
   *
   * One function for both rather than two nearly identical ones — the only
   * difference is whether a position inside a group is supplied, and two copies
   * is where the group version stops clearing something the flat one does.
   */
  const setRule = useCallback((key: string, patch: Partial<Rule>) => {
    setChildren((current) =>
      current.map((keyed) => {
        if (isKeyedGroup(keyed)) {
          // A group's own key never matches a rule's, so this only rewrites the
          // inner rule whose key was given.
          const any = keyed.any.map((inner) =>
            inner.key === key ? { key, rule: applyPatch(inner.rule, patch) } : inner,
          )
          return { key: keyed.key, any }
        }
        return keyed.key === key ? { key, rule: applyPatch(keyed.rule, patch) } : keyed
      }),
    )
  }, [])

  const addToGroup = useCallback(
    (key: string) => {
      setChildren((current) =>
        current.map((keyed) =>
          keyed.key === key && isKeyedGroup(keyed)
            ? { key, any: [...keyed.any, { key: mint(), rule: { ...EMPTY_RULE } }] }
            : keyed,
        ),
      )
    },
    [mint],
  )

  /** Removes one rule wherever it is: a top-level entry, or inside a group. */
  const removeRule = useCallback((key: string) => {
    setChildren((current) =>
      current
        .filter((keyed) => keyed.key !== key)
        .map((keyed) =>
          isKeyedGroup(keyed)
            ? { key: keyed.key, any: keyed.any.filter((inner) => inner.key !== key) }
            : keyed,
        ),
    )
  }, [])

  const save = useCallback(async () => {
    setSaving(true)
    setProblems([])
    try {
      const result = await client.saveSegment(
        // Spread rather than always present: absent is what tells the server to
        // leave the declared columns alone, and the builder page never has them.
        { name, definition, ...(custom === undefined ? {} : { custom }) },
        segmentId,
      )
      if (result.status === 'saved') {
        onSaved()
        return
      }
      setProblems(
        result.status === 'name-taken'
          ? ['A segment with that name already exists.']
          : result.problems,
      )
    } catch {
      setProblems(['We could not save that segment. Please try again in a moment.'])
    } finally {
      setSaving(false)
    }
  }, [client, custom, definition, name, onSaved, segmentId])

  return {
    /** `children` carries the render keys; `definition` is what is saved. */
    state: { name, definition, children, custom, reach, problems, saving } satisfies BuilderState,
    declared,
    setName,
    setCustom,
    setMatch,
    addRule,
    addGroup,
    removeRule,
    setRule,
    addToGroup,
    save,
  }
}
